import { useState, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, Form, Spinner } from 'react-bootstrap'
import parse from 'html-react-parser'

import {
  RESPONSE_TYPE_ASSESSMENT,
  RESPONSE_TYPE_ESSAY,
  RESPONSE_TYPE_SINGLE,
  getUserResponses,
  saveUserResponse
} from 'components/common/api/UserResponses'
import {
  EVENT_ACTION_SUBMIT,
  EVENT_CATEGORY_QUESTION,
  EVENT_CATEGORY_TOPIC,
  EVENT_CATEGORY_LESSON,
  EVENT_ACTION_COMPLETE,
  saveUserEvent
} from 'components/common/api/UserEvents'

import Confirmation from 'components/common/messaging/Confirmation'
import { getAssessmentOptionsFromQuestion } from 'components/common/api/Quizzes'
import { navigateTo } from 'components/common/navigation'
import { getQuizPath, getTopicPath } from 'components/common/helpers/Paths'
import QuestionAssessment from '../QuestionAssessment'
import QuestionSingleChoice from '../QuestionSingleChoice'
import QuestionEssay from '../QuestionEssay'
import {
  ACTION_COMPLETE,
  tryUpdateCourseProgressForCourseObject
} from 'components/common/api/CourseProgress'
import { LMS_TYPE_TOPIC, LMS_TYPE_LESSON } from 'components/common/api/Courses'
import { buildRequestURL } from 'components/common/api'

/**
 * QuestionSet component. Used for displaying a set of questions.
 */
export default function QuestionSet({
  questions,
  quiz,
  quizzes,
  topics,
  courseId,
  lessonId,
  topicId,
  activeQuestion,
  resultsMessages,
  setActiveQuestion,
  autoAdvance = true
}) {
  const history = useHistory()
  const [percentScore, setPercentScore] = useState(0)
  const [responses, setResponses] = useState()
  const [loading, setLoading] = useState(true)

  /** Helper function to determine whether or not the passed in topicIndex is the last topic in the lesson
   * @param {Number} topicIndex - The index of the topic to check
   * @returns {Boolean} - True if the topic is the last topic in the lesson, false otherwise
   */
  const isLastTopic = topicIndex =>
    topicIndex >= 0 && topicIndex === parseInt(topics.length ?? 0) - 1

  /** Helper function to determine whether or not the passed in quizIndex is the last quiz in the topic
   * @param {Number} quizIndex - The index of the quiz to check
   * @returns {Boolean} - True if the quiz is the last quiz in the topic, false otherwise
   */
  const isLastQuiz = quizIndex =>
    quizIndex >= 0 && quizIndex === parseInt(quizzes.length ?? 0) - 1

  /**
   * Helper function to get the first response for a given question
   * @param {Number} questionId
   */
  const getQuestionResponseText = useCallback(
    questionId => {
      // Return empty responses if we haven't loaded the data yet
      if (!responses || !responses.length) return ''

      const questionResponse = responses.find(
        response => response.question_id === questionId
      )

      return questionResponse ? questionResponse.response_text : ''
    },
    [responses]
  )

  /**
   * Helper function to update the score every time the user selects an answer
   */
  const updateScore = useCallback(() => {
    // If we don't have any responses, then there's nothing to calculate
    if (!responses || !responses.length) return

    let sum = 0,
      total = 0

    questions.forEach(question => {
      const answers = question._answerData
      const response = getQuestionResponseText(question.question_id)

      // Increment the total possible
      total += question._points

      // If we don't have a response for this question, skip it
      if (!response) return

      let points = 0

      // Did we specify to use custom points per answer?
      if (!question._answerPointsActivated) {
        // No
        points = question._points
      } else {
        const options =
          question._answerType === RESPONSE_TYPE_ASSESSMENT
            ? getAssessmentOptionsFromQuestion(question)
            : []
        // Yes
        answers.forEach(answer => {
          // Is this an assessment question?
          if (question._answerType === RESPONSE_TYPE_ASSESSMENT) {
            // Use the answer index within the assessment as the point score
            const index = options.findIndex(option => option === response)
            points = index >= 0 ? index + 1 : 0
          } else if (answer._answer === response && answer._correct) {
            // Does the response match the correct answer?
            points = answer._points
            return
          }
        })
      }

      sum += points
    }, 0)

    // How much did we get out of the total?
    const percent = total > 0 ? Math.round((sum / total) * 100) : 0

    setPercentScore(percent)
  }, [questions, responses, getQuestionResponseText])

  /**
   * Function to Save (Create) or Update the user's response
   * @param {Number} quizId
   * @param {Number} questionId
   * @param {String} responseText
   */
  const saveOrUpdate = async (quizId, questionId, responseText) => {
    let success = false
    let updated = responses
    // Do we already have a saved version of this?
    const savedIndex = updated.findIndex(
      response => response.question_id === questionId
    )
    const responseId = savedIndex > -1 ? responses[savedIndex].id : null

    if (responseId) {
      // Drop the existing version
      updated.splice(savedIndex, 1)
    }

    const result = await saveUserResponse(
      quizId,
      questionId,
      responseText,
      responseId
    )

    // Did we save successfully?
    success = !!result

    if (success) {
      // Update array with posted result, but keep local text to prevent displaying encrypted response
      updated.push({ ...result, response_text: responseText })
      setResponses(updated)
      updateScore()
    }

    // Log question responses
    saveUserEvent(EVENT_CATEGORY_QUESTION, EVENT_ACTION_SUBMIT, questionId)

    return success
  }

  /**
   * Event handler to trigger saving the response. Returns a boolean indicating whether the save was successful.
   * @param {Number} quizId The quiz ID that the question belongs to
   * @param {Number} questionId The question ID to save
   * @param {String} responseText The response text to save
   */
  const handleSaveResponse = async (quizId, questionId, responseText) => {
    // Short circuit if empty
    if (!responseText) return true

    const currentResponse = getQuestionResponseText(questionId)

    // Auto-advance to the next question, if we have multiple questions
    if (autoAdvance && questions.length > 1)
      setActiveQuestion(activeQuestion + 1)

    // Don't save empty responses or responses without a change
    responseText = responseText.trim()
    if (!responseText || responseText === currentResponse) return true

    // Save the response
    return saveOrUpdate(quizId, questionId, responseText)
  }

  /**
   * Event handler for topic and lesson completion, marks the current topic and lesson as "completed"
   */
  const handleComplete = async topicId => {
    // Use our course progress helper to mark this topic and lesson as complete
    let success = await tryUpdateCourseProgressForCourseObject(
      courseId,
      { type: LMS_TYPE_TOPIC, id: topicId },
      ACTION_COMPLETE
    )

    // Log topic completion with an event
    await saveUserEvent(EVENT_CATEGORY_TOPIC, EVENT_ACTION_COMPLETE, topicId)

    // If we we didn't successfully mark the topic as complete, then let's return
    if (!success) return

    success = await tryUpdateCourseProgressForCourseObject(
      courseId,
      { type: LMS_TYPE_LESSON, id: lessonId },
      ACTION_COMPLETE
    )

    // Log lesson completion with an event
    await saveUserEvent(EVENT_CATEGORY_LESSON, EVENT_ACTION_COMPLETE, lessonId)

    // Set the redirect URL to go back to the topic, but with the completed flag set
    const redirectUrl = buildRequestURL(
      getTopicPath(courseId, lessonId, topicId),
      { completed: true }
    )

    // Finally, let's redirect to the lesson complete page
    return navigateTo(redirectUrl, history)
  }

  /**
   * Event handler to proceed to next activity or next topic, or allow mark as complete if it's the very last activity
   */
  const handleContinue = async () => {
    // Return early if we don't have topics or quizzes loaded
    if (!topics?.length || !quizzes?.length) return

    // Get the current topic index
    const topicIndex = topics.findIndex(match => match.id === parseInt(topicId))

    // Get the current quiz index
    const quizIndex = quizzes.findIndex(match => match.id === parseInt(quiz.id))

    // Set the redirect URL to default back to the topic
    let redirectUrl = getTopicPath(courseId, lessonId, topicId)

    // If we're not on the last quiz, we can just go to the next quiz
    if (!isLastQuiz(quizIndex)) {
      // Get the next quiz
      const nextQuizId = quizzes[quizIndex + 1].id

      // Navigate to the next quiz
      redirectUrl = getQuizPath(courseId, lessonId, topicId, nextQuizId)
    } else {
      // If we're on the last topic, we can mark the lesson as complete
      if (isLastTopic(topicIndex)) {
        const topicId = topics[topicIndex].id
        return handleComplete(topicId)
      }

      // Otherwise, move to the next topic
      const nextTopicId = topics[topicIndex + 1].id

      // Get the next topic URL
      redirectUrl = getTopicPath(courseId, lessonId, nextTopicId)
    }

    // If we somehow got here, let's just redirect back to the topic
    return navigateTo(redirectUrl, history)
  }

  /**
   * Helper function to determine whether or not to show a given question
   * @param {Number} questionIndex
   */
  const shouldShowQuestion = questionIndex => questionIndex === activeQuestion

  /**
   * Helper function to render the quiz summary based on the score
   */
  const renderSummary = () => {
    // If we don't have any custom messages, don't render anything
    if (!resultsMessages?.length) return null

    // Let's pull the responses and sort them in reverse
    const messages = resultsMessages.sort(
      (a, b) => a.min_percent - b.min_percent
    )
    let message = ''

    // Loop through the messages and find the first one that matches our score
    messages.every(msg => {
      // Is our score at least as good as the minimum required?
      if (percentScore >= msg.min_percent) message = msg.message

      // If we have a message, we can stop
      return !message
    })

    return message
  }

  /**
   * Helper function to render the appopriate component, based on the question type
   * @param {Object} question
   */
  const renderQuestion = question => {
    const types = {}
    // Inline helper
    const _save = response =>
      handleSaveResponse(quiz.id, question.question_id, response)

    // Journaling
    types[RESPONSE_TYPE_ESSAY] = (
      <QuestionEssay
        question={question}
        defaultValue={getQuestionResponseText(question.question_id)}
        handleSaveResponse={e => _save(e)}
      />
    )

    // Yes/No or True/False
    types[RESPONSE_TYPE_SINGLE] = (
      <QuestionSingleChoice
        question={question}
        defaultValue={getQuestionResponseText(question.question_id)}
        handleSaveResponse={e => _save(e)}
      />
    )

    // Likert-style assessment question
    types[RESPONSE_TYPE_ASSESSMENT] = (
      <QuestionAssessment
        question={question}
        defaultValue={getQuestionResponseText(question.question_id)}
        handleSaveResponse={e => _save(e)}
      />
    )

    // Return our question type or the default/fallback
    return types[question._answerType] || null
  }

  useEffect(() => {
    // Return early if we don't have a quiz defined or if we've already collected the responses
    if (!quiz || !questions) return

    // If we have responses, let's just update the score
    if (responses)
      return updateScore()

      // Get responses
    ;(async function getSavedResponses() {
      const savedResponses = await getUserResponses({ quizId: quiz.id })

      // Did we get a result?
      if (savedResponses.responses) setResponses(savedResponses.responses)

      setLoading(false)
    })()
  }, [quiz, questions, updateScore, responses])

  // No need to proceed with render if we don't have any questions
  if (!questions || !questions.length) return null

  return loading ? (
    <Spinner
      animation='border'
      role='status'
      className='app-spinner'
      variant='primary'
      size='lg'
    >
      <span className='visually-hidden'>Loading...</span>
    </Spinner>
  ) : (
    <>
      <Form
        noValidate
        className={`question-wrapper ${
          activeQuestion === questions.length ? 'summary-screen' : ''
        }`}
      >
        <h2 className='quiz-title mobile-hidden'>{parse(quiz.clean_title)}</h2>
        {questions.map((question, questionIndex) => (
          <Form.Group
            key={question.question_id}
            className={`question-group ${
              shouldShowQuestion(questionIndex) ? 'active' : ''
            }`}
          >
            {/* Only show the question title if it's different from the text */}
            {question._question !== question.question_post_title && (
              <h2 className='question'>
                {parse(question.question_post_title)}
              </h2>
            )}
            <div className='my-3'>{parse(question._question)}</div>
            {renderQuestion(question)}
          </Form.Group>
        ))}

        {/* If we only have one question in the quiz, let's show a "Continue" link */}
        {questions.length === 1 && (
          <Button
            className='quiz-continue-button'
            variant='primary-outline'
            onClick={handleContinue}
          >
            Continue Course
          </Button>
        )}
      </Form>

      {/* If we're on the last screen, let's show the confirmation and summary */}
      {activeQuestion === questions.length && (
        <div className='quiz-summary'>
          <Confirmation
            title={`${quiz.clean_title} Complete`}
            message={renderSummary()}
            btnText={`Restart ${quiz.clean_title}`}
            btnOnClick={() => setActiveQuestion(0)}
            linkText={`Continue`}
            linkOnClick={handleContinue}
          />
        </div>
      )}
    </>
  )
}
